import LiquidText, { liquidTone } from '../components/LiquidText';
import { LiquidDialog } from '../components/LiquidUI';
import DeleteIcon from '../components/DeleteIcon';
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

/* ============================================================
   HIRA — IDENTIFICAÇÃO DE PERIGOS E AVALIAÇÃO DE RISCOS
   (SGI — ISO 45001, 6.1.2). Fiel ao relatório do TSST:
   atividades/postos com riscos avaliados pelos índices
   D/T/F/E/S (1-5) e NR ponderado. O NR vem do TSST — a app
   regista, não recalcula. Propostas de alteração = fluxo
   SPROD → técnico (folha "REVISÕES SPROD").
   ============================================================ */

const NIVEIS = [
    { max: 41, label: 'Trivial', bg: '#f0fdf4', fg: '#166534', bar: '#86efac' },
    { max: 67, label: 'Tolerável', bg: '#e0f2fe', fg: '#075985', bar: '#7dd3fc' },
    { max: 81, label: 'Moderado', bg: '#fef9c3', fg: '#854d0e', bar: '#fde047' },
    { max: 92, label: 'Substancial', bg: '#ffedd5', fg: '#9a3412', bar: '#fdba74' },
    { max: 100, label: 'Intolerável', bg: '#fee2e2', fg: '#991b1b', bar: '#fca5a5' },
];
const nivelNR = (nr) => {
    if (nr == null || nr === '') return null;
    const v = parseFloat(nr);
    return NIVEIS.find(n => v <= n.max) || NIVEIS[NIVEIS.length - 1];
};

const INDICES = [
    { key: 'ind_f', label: 'F', titulo: 'Frequência (1 = < 1×/ano … 5 = diária)' },
    { key: 'ind_e', label: 'E', titulo: 'Exposição (% do tempo: 1 = ≤1% … 5 = >50%)' },
    { key: 'ind_s', label: 'S', titulo: 'Severidade (1 = sem lesão … 5 = morte/incapacidade)' },
    { key: 'ind_d', label: 'D', titulo: 'Deficiência das condições (1 = boas … 5 = inexistentes)' },
    { key: 'ind_t', label: 'T', titulo: 'Trabalhadores expostos (1 = ≤3% … 5 = >29%)' },
];

const ESTADO_PROPOSTA = {
    proposta: { label: 'Proposta', bg: '#fef9c3', fg: '#854d0e' },
    aprovada: { label: 'Aprovada', bg: '#dcfce7', fg: '#166534' },
    rejeitada: { label: 'Rejeitada', bg: '#fee2e2', fg: '#991b1b' },
};

const fmtData = (d) => { if (!d) return '—'; try { return new Date(d).toLocaleDateString('pt-PT'); } catch { return '—'; } };

/* ---------- Atividade (criar/editar) ---------- */
function AtividadeModal({ atividade, ano, onClose, onSaved }) {
    const novo = !atividade;
    const [form, setForm] = useState({
        codigo: atividade?.codigo || '', nome: atividade?.nome || '',
        tarefa: atividade?.tarefa || '', equipamentos: atividade?.equipamentos || '', produtos: atividade?.produtos || '',
    });
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');
    const upd = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

    const guardar = async () => {
        if (!form.codigo.trim() || !form.nome.trim()) { setErr('Código e nome são obrigatórios.'); return; }
        setSaving(true); setErr('');
        try {
            if (novo) await api.post('/sistema-integrado/hira/atividades', { ...form, ano });
            else await api.put(`/sistema-integrado/hira/atividades/${atividade.id}`, form);
            onSaved(); onClose();
        } catch (e) { setErr(e.response?.data?.error || 'Erro ao guardar'); }
        finally { setSaving(false); }
    };

    return (
        <LiquidDialog data-sgi-dialog="true" onClose={onClose} style={overlay} onClick={onClose}>
            <div style={{ ...modal, maxWidth: 640 }} onClick={e => e.stopPropagation()}>
                <div data-sgi-dialog-heading="true" style={modalHead}>
                    <h3 style={{ margin: 0 }}>{novo ? `Nova atividade (${ano})` : `Editar ${atividade.codigo}`}</h3>
                    <button data-liquid-close="true" aria-label="Fechar" onClick={onClose} style={closeBtn}><LiquidText>✕</LiquidText></button>
                </div>
                <div style={grid2}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Código *</label>
                        <input value={form.codigo} onChange={e => upd('codigo', e.target.value)} placeholder="ex.: TRA" style={{ ...inp, width: '100%' }} />
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Nome *</label>
                        <input value={form.nome} onChange={e => upd('nome', e.target.value)} placeholder="ex.: Trabalho no exterior" style={{ ...inp, width: '100%' }} />
                    </div>
                </div>
                <div style={{ marginBottom: 10 }}>
                    <label data-liquid-label="true" style={lbl}>Tarefa(s)</label>
                    <textarea value={form.tarefa} onChange={e => upd('tarefa', e.target.value)} rows={2} style={{ ...inp, width: '100%', resize: 'vertical' }} />
                </div>
                <div style={{ marginBottom: 10 }}>
                    <label data-liquid-label="true" style={lbl}>Equipamentos e utensílios</label>
                    <textarea value={form.equipamentos} onChange={e => upd('equipamentos', e.target.value)} rows={2} style={{ ...inp, width: '100%', resize: 'vertical' }} />
                </div>
                <div style={{ marginBottom: 6 }}>
                    <label data-liquid-label="true" style={lbl}>Produtos e matérias-primas</label>
                    <textarea value={form.produtos} onChange={e => upd('produtos', e.target.value)} rows={2} style={{ ...inp, width: '100%', resize: 'vertical' }} />
                </div>
                {err && <div style={{ color: '#e53e3e', margin: '10px 0', fontSize: '0.85rem' }}>{err}</div>}
                <div data-liquid-actions="true" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
                    <button data-liquid-secondary="true" onClick={onClose} style={btnSec}>Cancelar</button>
                    <button data-liquid-primary="true" onClick={guardar} disabled={saving} style={{ ...btnPri, opacity: saving ? 0.6 : 1 }}>{saving ? 'A guardar…' : 'Guardar'}</button>
                </div>
            </div>
        </LiquidDialog>
    );
}

/* ---------- Risco (criar/editar) ---------- */
function RiscoModal({ risco, atividade, onClose, onSaved }) {
    const novo = !risco;
    const [form, setForm] = useState({
        ref: risco?.ref || '', fator_risco: risco?.fator_risco || '', risco: risco?.risco || '',
        ind_f: risco?.ind_f ?? '', ind_e: risco?.ind_e ?? '', ind_s: risco?.ind_s ?? '',
        ind_d: risco?.ind_d ?? '', ind_t: risco?.ind_t ?? '', nr: risco?.nr ?? '',
        medidas_preventivas: risco?.medidas_preventivas || '', medidas_corretivas: risco?.medidas_corretivas || '',
    });
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');
    const upd = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
    const nv = nivelNR(form.nr);

    const guardar = async () => {
        if (!form.fator_risco.trim()) { setErr('Indique o fator de risco.'); return; }
        setSaving(true); setErr('');
        try {
            if (novo) await api.post(`/sistema-integrado/hira/atividades/${atividade.id}/riscos`, form);
            else await api.put(`/sistema-integrado/hira/riscos/${risco.id}`, form);
            onSaved(); onClose();
        } catch (e) { setErr(e.response?.data?.error || 'Erro ao guardar'); }
        finally { setSaving(false); }
    };

    return (
        <LiquidDialog data-sgi-dialog="true" onClose={onClose} style={overlay} onClick={onClose}>
            <div style={{ ...modal, maxWidth: 700 }} onClick={e => e.stopPropagation()}>
                <div data-sgi-dialog-heading="true" style={modalHead}>
                    <h3 style={{ margin: 0 }}>{novo ? `Novo risco — ${atividade.codigo}` : `Editar ${risco.ref || 'risco'}`}</h3>
                    <button data-liquid-close="true" aria-label="Fechar" onClick={onClose} style={closeBtn}><LiquidText>✕</LiquidText></button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: 10, marginBottom: 10 }}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Ref.ª</label>
                        <input value={form.ref} onChange={e => upd('ref', e.target.value)} placeholder="ex.: TRA38" style={{ ...inp, width: '100%' }} />
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Fator de risco *</label>
                        <input value={form.fator_risco} onChange={e => upd('fator_risco', e.target.value)} style={{ ...inp, width: '100%' }} />
                    </div>
                </div>
                <div style={{ marginBottom: 10 }}>
                    <label data-liquid-label="true" style={lbl}>Risco(s)</label>
                    <input value={form.risco} onChange={e => upd('risco', e.target.value)} placeholder="ex.: Queda em altura" style={{ ...inp, width: '100%' }} />
                </div>

                <label data-liquid-label="true" style={lbl}>Índices (1-5) e nível de risco — o NR é calculado pelo TSST</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 12 }}>
                    {INDICES.map(ix => (
                        <div key={ix.key} title={ix.titulo}>
                            <label style={{ ...lbl, textAlign: 'center' }}><LiquidText>{ix.label}</LiquidText></label>
                            <select value={form[ix.key]} onChange={e => upd(ix.key, e.target.value)} style={{ ...inp, width: 58 }}>
                                <option value="">—</option>
                                {[1, 2, 3, 4, 5].map(v => <option key={v} value={v}>{v}</option>)}
                            </select>
                        </div>
                    ))}
                    <div>
                        <label style={{ ...lbl, textAlign: 'center' }}>NR</label>
                        <input type="number" step="0.01" min="0" max="100" value={form.nr} onChange={e => upd('nr', e.target.value)} style={{ ...inp, width: 90 }} />
                    </div>
                    {nv && <span data-liquid-tone={liquidTone(nv.bg)} data-liquid-badge="true" style={{ ...badge, background: nv.bg, color: nv.fg, border: `1px solid ${nv.bar}`, marginBottom: 6 }}><LiquidText>{nv.label}</LiquidText></span>}
                </div>

                <div style={grid2}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Medidas preventivas</label>
                        <textarea value={form.medidas_preventivas} onChange={e => upd('medidas_preventivas', e.target.value)} rows={4} style={{ ...inp, width: '100%', resize: 'vertical' }} />
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Medidas corretivas</label>
                        <textarea value={form.medidas_corretivas} onChange={e => upd('medidas_corretivas', e.target.value)} rows={4} style={{ ...inp, width: '100%', resize: 'vertical' }} />
                    </div>
                </div>
                {err && <div style={{ color: '#e53e3e', margin: '10px 0', fontSize: '0.85rem' }}>{err}</div>}
                <div data-liquid-actions="true" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
                    <button data-liquid-secondary="true" onClick={onClose} style={btnSec}>Cancelar</button>
                    <button data-liquid-primary="true" onClick={guardar} disabled={saving} style={{ ...btnPri, opacity: saving ? 0.6 : 1 }}>{saving ? 'A guardar…' : 'Guardar'}</button>
                </div>
            </div>
        </LiquidDialog>
    );
}

/* ---------- Propostas de alteração (fluxo SPROD → TSST) ---------- */
function PropostasModal({ onClose }) {
    const [propostas, setPropostas] = useState(null);
    const [nova, setNova] = useState({ referencia: '', descricao: '', data: new Date().toISOString().substring(0, 10) });
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');

    const carregar = useCallback(async () => {
        try { const r = await api.get('/sistema-integrado/hira/propostas'); setPropostas(r.data || []); }
        catch { setPropostas([]); }
    }, []);
    useEffect(() => { carregar(); }, [carregar]);

    const adicionar = async () => {
        if (!nova.descricao.trim()) { setErr('Indique a descrição da alteração proposta.'); return; }
        setSaving(true); setErr('');
        try {
            await api.post('/sistema-integrado/hira/propostas', nova);
            setNova({ referencia: '', descricao: '', data: new Date().toISOString().substring(0, 10) });
            carregar();
        } catch (e) { setErr(e.response?.data?.error || 'Erro ao criar'); }
        finally { setSaving(false); }
    };

    const mudarEstado = async (p, estado) => {
        const aprovado_por = estado === 'aprovada' ? (window.prompt('Aprovada por (técnico):', p.aprovado_por || '') || null) : p.aprovado_por;
        try {
            await api.put(`/sistema-integrado/hira/propostas/${p.id}`, {
                ...p, estado, aprovado_por,
                data_aprovacao: estado === 'aprovada' ? new Date().toISOString().substring(0, 10) : p.data_aprovacao,
            });
            carregar();
        } catch { alert('Erro ao atualizar'); }
    };

    const eliminar = async (p) => {
        if (!window.confirm('Eliminar esta proposta?')) return;
        try { await api.delete(`/sistema-integrado/hira/propostas/${p.id}`); carregar(); }
        catch { alert('Erro ao eliminar'); }
    };

    return (
        <LiquidDialog data-sgi-dialog="true" onClose={onClose} style={overlay} onClick={onClose}>
            <div style={{ ...modal, maxWidth: 760 }} onClick={e => e.stopPropagation()}>
                <div data-sgi-dialog-heading="true" style={modalHead}>
                    <h3 style={{ margin: 0 }}>Propostas de alteração à avaliação de riscos</h3>
                    <button data-liquid-close="true" aria-label="Fechar" onClick={onClose} style={closeBtn}><LiquidText>✕</LiquidText></button>
                </div>
                <p style={{ color: '#64748b', fontSize: '0.85rem', marginTop: 0 }}>
                    Alterações propostas pela SPROD à matriz do TSST — aguardam aprovação do técnico na revisão seguinte.
                </p>

                {propostas === null ? <p style={{ color: '#64748b' }}>A carregar…</p> : propostas.length === 0 ? (
                    <p style={{ color: '#94a3b8', fontSize: '0.88rem' }}>Sem propostas registadas.</p>
                ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', marginBottom: 14 }}>
                        <thead>
                            <tr style={{ textAlign: 'left', color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>
                                <th style={{ padding: '4px 6px' }}>Ref.ª</th>
                                <th style={{ padding: '4px 6px' }}>Descrição</th>
                                <th style={{ padding: '4px 6px' }}>Data</th>
                                <th style={{ padding: '4px 6px' }}>Estado</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {propostas.map(p => {
                                const es = ESTADO_PROPOSTA[p.estado] || ESTADO_PROPOSTA.proposta;
                                return (
                                    <tr key={p.id} style={{ borderBottom: '1px solid #f8fafc' }}>
                                        <td style={{ padding: '6px', fontWeight: 700, whiteSpace: 'nowrap' }}>{p.referencia || '—'}</td>
                                        <td style={{ padding: '6px' }}>{p.descricao}</td>
                                        <td style={{ padding: '6px', whiteSpace: 'nowrap', color: '#64748b' }}>{fmtData(p.data)}</td>
                                        <td style={{ padding: '6px' }}>
                                            <span data-liquid-tone={liquidTone(es.bg)} data-liquid-badge="true" style={{ ...badge, background: es.bg, color: es.fg }} title={p.aprovado_por ? `Por ${p.aprovado_por} em ${fmtData(p.data_aprovacao)}` : ''}><LiquidText>{es.label}</LiquidText></span>
                                        </td>
                                        <td style={{ padding: '6px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                            {p.estado === 'proposta' && (
                                                <>
                                                    <button data-liquid-secondary="true" onClick={() => mudarEstado(p, 'aprovada')} style={{ ...btnSec, padding: '3px 8px', fontSize: '0.72rem' }} title="Marcar aprovada pelo técnico"><LiquidText>✓</LiquidText></button>
                                                    <button data-liquid-secondary="true" onClick={() => mudarEstado(p, 'rejeitada')} style={{ ...btnSec, padding: '3px 8px', fontSize: '0.72rem', marginLeft: 4 }} title="Marcar rejeitada"><LiquidText>✗</LiquidText></button>
                                                </>
                                            )}
                                            <button data-liquid-secondary="true" onClick={() => eliminar(p)} style={{ ...btnSec, padding: '3px 8px', fontSize: '0.72rem', color: '#e53e3e', marginLeft: 4 }} aria-label="Apagar" title="Apagar"><DeleteIcon /></button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}

                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', borderTop: '1px solid #f1f5f9', paddingTop: 12 }}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Ref.ª</label>
                        <input value={nova.referencia} onChange={e => setNova(prev => ({ ...prev, referencia: e.target.value }))} placeholder="ex.: TRA10" style={{ ...inp, width: 90 }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 200 }}>
                        <label data-liquid-label="true" style={lbl}>Descrição da alteração proposta</label>
                        <input value={nova.descricao} onChange={e => setNova(prev => ({ ...prev, descricao: e.target.value }))} style={{ ...inp, width: '100%' }} />
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Data</label>
                        <input type="date" value={nova.data} onChange={e => setNova(prev => ({ ...prev, data: e.target.value }))} style={inp} />
                    </div>
                    <button data-liquid-primary="true" onClick={adicionar} disabled={saving} style={{ ...btnPri, opacity: saving ? 0.6 : 1 }}>+ Propor</button>
                </div>
                {err && <div style={{ color: '#e53e3e', marginTop: 8, fontSize: '0.85rem' }}>{err}</div>}
            </div>
        </LiquidDialog>
    );
}

/* ---------- Componente principal ---------- */
const Hira = () => {
    const anoAtual = new Date().getFullYear();
    const [ano, setAno] = useState(anoAtual);
    const [anosExistentes, setAnosExistentes] = useState([]);
    const [atividades, setAtividades] = useState([]);
    const [loading, setLoading] = useState(true);
    const [abertas, setAbertas] = useState(() => new Set());
    const [pesquisa, setPesquisa] = useState('');
    const [nivelFiltro, setNivelFiltro] = useState('');
    const [modalAtividade, setModalAtividade] = useState(null);   // {atividade|undefined}
    const [modalRisco, setModalRisco] = useState(null);           // {risco|undefined, atividade}
    const [mostrarPropostas, setMostrarPropostas] = useState(false);
    const [expandido, setExpandido] = useState(null);             // risco id com medidas abertas

    const carregar = useCallback(async () => {
        setLoading(true);
        try {
            const [r, ra] = await Promise.all([
                api.get('/sistema-integrado/hira', { params: { ano } }),
                api.get('/sistema-integrado/hira/anos'),
            ]);
            setAtividades(r.data || []);
            setAnosExistentes(Array.isArray(ra.data) ? ra.data : []);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, [ano]);
    useEffect(() => { carregar(); }, [carregar]);

    const anos = [];
    for (let a = anoAtual + 1; a >= anoAtual - 4; a--) anos.push(a);
    anosExistentes.forEach(a => { if (!anos.includes(a)) anos.push(a); });
    anos.sort((x, y) => y - x);

    const toggle = (id) => setAbertas(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

    const eliminarAtividade = async (a) => {
        if (!window.confirm(`Eliminar a atividade "${a.codigo} — ${a.nome}" e os seus ${a.riscos.length} riscos?`)) return;
        try { await api.delete(`/sistema-integrado/hira/atividades/${a.id}`); carregar(); }
        catch { alert('Erro ao eliminar'); }
    };
    const eliminarRisco = async (r) => {
        if (!window.confirm(`Eliminar o risco ${r.ref || ''} "${r.fator_risco}"?`)) return;
        try { await api.delete(`/sistema-integrado/hira/riscos/${r.id}`); carregar(); }
        catch { alert('Erro ao eliminar'); }
    };

    const todosRiscos = atividades.flatMap(a => a.riscos);
    const contagemNivel = NIVEIS.map(n => ({ ...n, count: todosRiscos.filter(r => nivelNR(r.nr)?.label === n.label).length }));

    const filtraRiscos = (riscos) => {
        let v = riscos;
        if (nivelFiltro) v = v.filter(r => nivelNR(r.nr)?.label === nivelFiltro);
        if (pesquisa.trim()) {
            const t = pesquisa.trim().toLowerCase();
            v = v.filter(r => (r.ref || '').toLowerCase().includes(t) || (r.fator_risco || '').toLowerCase().includes(t) ||
                (r.risco || '').toLowerCase().includes(t) || (r.medidas_preventivas || '').toLowerCase().includes(t));
        }
        return v;
    };
    const filtrando = !!(nivelFiltro || pesquisa.trim());

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Ano</label>
                        <select value={ano} onChange={e => setAno(parseInt(e.target.value, 10))} style={inp}>
                            {anos.map(a => <option key={a} value={a}>{a}</option>)}
                        </select>
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Pesquisar</label>
                        <input value={pesquisa} onChange={e => setPesquisa(e.target.value)} placeholder="Ref, fator, risco…" style={{ ...inp, width: 190 }} />
                    </div>
                </div>
                <div data-liquid-actions="true" style={{ display: 'flex', gap: 8 }}>
                    <button data-liquid-secondary="true" onClick={() => setMostrarPropostas(true)} style={btnSec}><LiquidText>📝 Propostas de alteração</LiquidText></button>
                    <button data-liquid-primary="true" onClick={() => setModalAtividade({})} style={btnPri}>+ Nova atividade</button>
                </div>
            </div>

            {/* Distribuição por nível — clicável para filtrar */}
            {todosRiscos.length > 0 && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                    {contagemNivel.map(n => (
                        <button key={n.label} onClick={() => setNivelFiltro(nivelFiltro === n.label ? '' : n.label)}
                            style={{
                                flex: '1 1 110px', background: n.bg, border: `2px solid ${nivelFiltro === n.label ? n.fg : n.bar}`,
                                borderRadius: 10, padding: '9px 12px', cursor: 'pointer', textAlign: 'center',
                            }}>
                            <div style={{ fontSize: '1.3rem', fontWeight: 800, color: n.fg }}>{n.count}</div>
                            <div style={{ fontSize: '0.78rem', color: n.fg }}><LiquidText>{n.label}</LiquidText></div>
                        </button>
                    ))}
                </div>
            )}

            {loading ? <p style={{ color: '#718096' }}>A carregar…</p>
                : atividades.length === 0 ? (
                    <div style={{ padding: '48px 0', textAlign: 'center', color: '#94a3b8' }}>
                        Sem avaliação de riscos em {ano}. Crie as atividades/postos de trabalho.
                    </div>
                ) : atividades.map(a => {
                    const riscosVisiveis = filtraRiscos(a.riscos);
                    if (filtrando && riscosVisiveis.length === 0) return null;
                    const aberta = filtrando || abertas.has(a.id);
                    const maxNr = a.riscos.reduce((m, r) => Math.max(m, parseFloat(r.nr) || 0), 0);
                    const nvMax = nivelNR(maxNr || null);
                    return (
                        <div key={a.id} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, marginBottom: 10, overflow: 'hidden' }}>
                            <div onClick={() => toggle(a.id)}
                                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', cursor: 'pointer', flexWrap: 'wrap' }}>
                                <span style={{ color: '#94a3b8', fontSize: '0.8rem', width: 14 }}><LiquidText>{aberta ? '▼' : '▶'}</LiquidText></span>
                                <strong style={{ color: '#1e293b', minWidth: 46 }}>{a.codigo}</strong>
                                <span style={{ flex: 1, color: '#334155' }}>{a.nome.replace(/^[A-Z]{2,4}\s*-\s*/, '')}</span>
                                <span data-liquid-tone={liquidTone('#f1f5f9')} data-liquid-badge="true" style={{ ...badge, background: '#f1f5f9', color: '#475569' }}>{a.riscos.length} risco{a.riscos.length === 1 ? '' : 's'}</span>
                                {nvMax && <span data-liquid-tone={liquidTone(nvMax.bg)} data-liquid-badge="true" style={{ ...badge, background: nvMax.bg, color: nvMax.fg }} title={`NR máximo: ${maxNr.toFixed(1)}`}>máx. <LiquidText>{nvMax.label}</LiquidText></span>}
                                <span onClick={e => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                                    <button data-liquid-secondary="true" onClick={() => setModalRisco({ atividade: a })} style={{ ...btnSec, ...mini }} title="Adicionar risco">+ risco</button>
                                    <button data-liquid-secondary="true" onClick={() => setModalAtividade({ atividade: a })} style={{ ...btnSec, ...mini }} title="Editar atividade">Editar</button>
                                    <button data-liquid-secondary="true" onClick={() => eliminarAtividade(a)} style={{ ...btnSec, ...mini, color: '#e53e3e' }} title="Eliminar atividade"><DeleteIcon /></button>
                                </span>
                            </div>
                            {aberta && (
                                <div style={{ borderTop: '1px solid #f1f5f9', overflowX: 'auto' }}>
                                    {(a.tarefa || a.equipamentos || a.produtos) && (
                                        <div style={{ padding: '8px 16px', fontSize: '0.8rem', color: '#64748b', background: '#f8fafc' }}>
                                            {a.tarefa && <div><strong>Tarefa:</strong> {a.tarefa}</div>}
                                            {a.equipamentos && <div><strong>Equipamentos:</strong> {a.equipamentos}</div>}
                                            {a.produtos && <div><strong>Produtos:</strong> {a.produtos}</div>}
                                        </div>
                                    )}
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                        <thead>
                                            <tr style={{ textAlign: 'left', color: '#64748b', background: '#f8fafc' }}>
                                                <th style={{ padding: '6px 10px' }}>Ref.ª</th>
                                                <th style={{ padding: '6px 10px' }}>Fator de risco</th>
                                                <th style={{ padding: '6px 10px' }}>Risco</th>
                                                {INDICES.map(ix => <th key={ix.key} title={ix.titulo} style={{ padding: '6px 4px', textAlign: 'center', width: 26 }}><LiquidText>{ix.label}</LiquidText></th>)}
                                                <th style={{ padding: '6px 10px', textAlign: 'center' }}>NR</th>
                                                <th style={{ padding: '6px 10px', textAlign: 'right' }}></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {riscosVisiveis.map(r => {
                                                const nv = nivelNR(r.nr);
                                                const aberto = expandido === r.id;
                                                return (
                                                    <React.Fragment key={r.id}>
                                                        <tr onClick={() => setExpandido(aberto ? null : r.id)} style={{ borderTop: '1px solid #f1f5f9', cursor: 'pointer' }}>
                                                            <td style={{ padding: '6px 10px', fontWeight: 700, whiteSpace: 'nowrap' }}>{r.ref || '—'}</td>
                                                            <td style={{ padding: '6px 10px', maxWidth: 260 }}>{r.fator_risco}</td>
                                                            <td style={{ padding: '6px 10px', maxWidth: 220, color: '#475569' }}>{r.risco || '—'}</td>
                                                            {INDICES.map(ix => <td key={ix.key} style={{ padding: '6px 4px', textAlign: 'center', color: '#64748b' }}>{r[ix.key] ?? '—'}</td>)}
                                                            <td style={{ padding: '6px 10px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                                {nv ? <span data-liquid-tone={liquidTone(nv.bg)} data-liquid-badge="true" style={{ ...badge, background: nv.bg, color: nv.fg }} title={`NR ${parseFloat(r.nr).toFixed(1)} — ${nv.label}`}>{parseFloat(r.nr).toFixed(0)}</span> : '—'}
                                                            </td>
                                                            <td style={{ padding: '6px 10px', textAlign: 'right', whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                                                                <button data-liquid-secondary="true" onClick={() => setModalRisco({ risco: r, atividade: a })} style={{ ...btnSec, ...mini }} title="Editar"><LiquidText>✎</LiquidText></button>
                                                                <button data-liquid-secondary="true" onClick={() => eliminarRisco(r)} style={{ ...btnSec, ...mini, color: '#e53e3e' }} title="Eliminar"><DeleteIcon /></button>
                                                            </td>
                                                        </tr>
                                                        {aberto && (
                                                            <tr style={{ background: '#fcfcfd' }}>
                                                                <td colSpan={5 + INDICES.length} style={{ padding: '8px 16px 12px 34px', fontSize: '0.82rem', color: '#475569' }}>
                                                                    <div style={{ marginBottom: 6 }}><strong>Medidas preventivas:</strong><br />{r.medidas_preventivas || '—'}</div>
                                                                    <div><strong>Medidas corretivas:</strong><br />{r.medidas_corretivas || '—'}</div>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    );
                })}

            <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: 10 }}>
                Metodologia do TSST (4.STSA.I.038): índices 1-5 — F frequência · E exposição · S severidade · D deficiência · T trabalhadores expostos.
                NR: ≤41 Trivial · ≤67 Tolerável · ≤81 Moderado · ≤92 Substancial · ≤100 Intolerável. Clicar num risco mostra as medidas.
            </p>

            {modalAtividade && <AtividadeModal atividade={modalAtividade.atividade} ano={ano} onClose={() => setModalAtividade(null)} onSaved={carregar} />}
            {modalRisco && <RiscoModal risco={modalRisco.risco} atividade={modalRisco.atividade} onClose={() => setModalRisco(null)} onSaved={carregar} />}
            {mostrarPropostas && <PropostasModal onClose={() => setMostrarPropostas(false)} />}
        </div>
    );
};

/* ---------- estilos ---------- */
const lbl = { display: 'block', fontSize: '0.74rem', color: '#64748b', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.03em' };
const inp = { padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.9rem', boxSizing: 'border-box' };
const badge = { padding: '3px 10px', borderRadius: 12, fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap' };
const btnSec = { padding: '7px 14px', border: '1px solid #cbd5e1', background: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem', color: '#334155' };
const btnPri = { padding: '7px 14px', border: '1px solid #3182ce', background: '#3182ce', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem' };
const mini = { padding: '4px 9px', marginLeft: 6, fontSize: '0.76rem' };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '40px 16px', overflowY: 'auto' };
const modal = { background: '#fff', borderRadius: 14, padding: 22, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' };
const modalHead = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 };
const closeBtn = { border: 'none', background: 'transparent', fontSize: '1.2rem', cursor: 'pointer', color: '#64748b' };
const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 };

export default Hira;
